"use client";

import { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import { parseCoords, formatAll } from "@/lib/freeTools/vector3";
import { ShowTourButton } from "@/components/freeTools/ShowTourButton";
import { ToolWalkthrough } from "@/components/walkthrough/ToolWalkthrough";
import {
  finishLabel as vector3FinishLabel,
  steps as vector3Steps,
} from "@/components/walkthrough/steps/vector3";

const EXAMPLES = [
  "vector3(-425.4, 1123.8, 325.8)",
  "X: 215.76  Y: -810.25  Z: 30.73",
  "441.1, -982.4, 30.6, 180.0",
];

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label="Copy to clipboard"
      onClick={() => {
        try {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* clipboard unavailable — no-op */
        }
      }}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground"
    >
      {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export function Vector3Client() {
  const [input, setInput] = useState("vector3(-425.4, 1123.8, 325.8)");
  const [precision, setPrecision] = useState(2);
  // Manual mode only: nothing here ever auto-starts over coordinates being converted.
  const [tourSignal, setTourSignal] = useState(0);

  const coords = useMemo(() => parseCoords(input), [input]);
  const outputs = useMemo(
    () => (coords ? formatAll(coords, precision) : []),
    [coords, precision],
  );

  return (
    <div className="not-prose rounded-2xl border border-border/60 bg-muted/30 p-5 sm:p-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <label htmlFor="vec3-input" className="block text-sm font-medium text-foreground">
          Paste coordinates (any format)
        </label>
        <ShowTourButton
          testId="vector3-show-tour"
          onClick={() => setTourSignal((signal) => signal + 1)}
        />
      </div>
      <textarea
        id="vec3-input"
        data-walkthrough="crd-input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        spellCheck={false}
        rows={2}
        placeholder="e.g. -425.4, 1123.8, 325.8"
        className="w-full resize-y rounded-xl border border-border bg-background px-4 py-3 font-mono text-sm text-foreground outline-none transition-colors focus:border-primary/50"
      />

      <div
        data-walkthrough="crd-examples"
        className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
      >
        <span className="mr-1">Try:</span>
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            onClick={() => setInput(ex)}
            className="rounded-md border border-border/60 px-2 py-0.5 font-mono transition-colors hover:border-primary/30 hover:text-foreground"
          >
            {ex}
          </button>
        ))}
      </div>

      <div
        data-walkthrough="crd-decimals"
        className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"
      >
        <label htmlFor="vec3-precision">Decimals</label>
        <input
          id="vec3-precision"
          type="number"
          min={0}
          max={6}
          value={precision}
          onChange={(e) =>
            setPrecision(Math.max(0, Math.min(6, Number(e.target.value) || 0)))
          }
          className="w-16 rounded-md border border-border bg-background px-2 py-1 text-center font-mono text-foreground outline-none focus:border-primary/50"
        />
      </div>

      {coords ? (
        <div data-walkthrough="crd-results" className="mt-5 space-y-2.5">
          {outputs.map((o) => (
            <div
              key={o.format}
              className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3"
            >
              <span className="w-28 shrink-0 text-xs font-medium text-muted-foreground">
                {o.label}
              </span>
              <code className="min-w-0 flex-1 truncate font-mono text-sm text-foreground">
                {o.value}
              </code>
              <CopyButton value={o.value} />
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">
          Paste at least three numbers (x, y, z) to see formatted output. Add a fourth for a
          heading (vector4).
        </p>
      )}

      <ToolWalkthrough
        steps={vector3Steps}
        storageKey="workshop_tour_coordinates_v1"
        finishLabel={vector3FinishLabel}
        openSignal={tourSignal}
      />
    </div>
  );
}
